/**
 * Teslyn Cloud's Space weather page: the provider proxy with the signed-in person's token (the same proxy and
 * operations as Teslyn Field's screen: BoM with the organisation's own key, decision 687; the public sources; staff
 * only, decisions 688, 691), and the organisation's projects with their blocks, read through RLS.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  ENLIL_BUCKET,
  ENLIL_MANIFEST,
  ProxyError,
  parseEnlilManifest,
  type Block,
  type ProxyAnswer,
  type ProxyClient,
} from '@teslyn/domain';
import { swpcFrameUrl, type EnlilSource } from './EnlilPlayer.tsx';
import { readConfig } from '../config.ts';
import { all, plainPortalError } from '../portal/api.ts';

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

const PROXY_CODES = new Set([
  'not_set_up',
  'choose_account',
  'forbidden',
  'rate_limited',
  'credential_refused',
  'provider_error',
]);

/** The provider proxy as the signed-in person, for the organisation `tenantId`. */
export function webProxyClient(
  sb: SupabaseClient,
  tenantId: string,
  fetchImpl: Fetch = (u, i) => fetch(u, i),
  cfg: { readonly supabaseUrl: string; readonly supabasePublishableKey: string } = readConfig(),
): ProxyClient {
  return {
    async call(provider, label, op, params = {}): Promise<ProxyAnswer> {
      let jwt: string | null = null;
      try {
        jwt = (await sb.auth.getSession()).data.session?.access_token ?? null;
      } catch {
        jwt = null;
      }
      if (!jwt) throw new ProxyError('signed_out', 'Sign in again to get space weather.');
      let res: Response;
      try {
        res = await fetchImpl(`${cfg.supabaseUrl.replace(/\/+$/, '')}/functions/v1/provider-proxy`, {
          method: 'POST',
          headers: {
            apikey: cfg.supabasePublishableKey,
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ tenant_id: tenantId, provider, label, op, params }),
        });
      } catch {
        throw new ProxyError('offline', 'Teslyn Cloud cannot be reached.');
      }
      if (res.status === 401) throw new ProxyError('signed_out', 'Sign in again to get space weather.');
      const j = (await res.json().catch(() => null)) as {
        code?: string;
        error?: string;
        body?: string;
        fetched_at?: string;
        cached?: boolean;
      } | null;
      if (!res.ok || !j || typeof j.body !== 'string') {
        const code = j?.code && PROXY_CODES.has(j.code) ? j.code : 'provider_error';
        throw new ProxyError(
          code as ConstructorParameters<typeof ProxyError>[0],
          j?.error ?? `Teslyn Cloud answered HTTP ${res.status}.`,
          res.status,
        );
      }
      return { body: j.body, fetchedAt: j.fetched_at ?? new Date().toISOString(), cached: Boolean(j.cached) };
    },
  };
}

/** A project the page can be shown for. */
export interface SwProject {
  readonly id: string;
  readonly number: string;
  readonly title: string;
  readonly status: string;
  readonly qc_overrides: Record<string, unknown> | null;
  readonly qc_rules: unknown;
}

const ORDER: Readonly<Record<string, number>> = { active: 0, mobilising: 1, planned: 2, complete: 3 };

/**
 * The organisation's projects, not deleted and not archived: In progress first, then Mobilising, then the others,
 * each by number (the page opens on the first, the one being flown).
 */
export async function loadSpaceWeatherProjects(sb: SupabaseClient, tenantId: string): Promise<SwProject[]> {
  const { data, error } = await sb
    .from('project')
    .select('id, number, title, status, qc_overrides, qc_rules')
    .eq('tenant_id', tenantId)
    .is('deleted_at', null)
    .neq('status', 'archived')
    .order('number');
  if (error) throw new Error(plainPortalError(error));
  return ((data ?? []) as SwProject[])
    .slice()
    .sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) || a.number.localeCompare(b.number));
}

/** A project's blocks (for its centre, the nearest BoM K sites and the daylight). */
export async function loadProjectBlocks(sb: SupabaseClient, projectId: string): Promise<Block[]> {
  return all<Block>((from, to) =>
    sb
      .from('block')
      .select('*')
      .eq('project_id', projectId)
      .is('deleted_at', null)
      .order('name')
      .range(from, to),
  );
}

/**
 * WSA-Enlil frames: Teslyn Cloud's copy of the latest run (bucket `space-weather`, staff only) when its manifest
 * names a complete run, each frame downloaded as the signed-in person and shown as a `blob:` address; a frame the
 * copy cannot give comes from SWPC instead.
 */
export function webEnlilSource(sb: SupabaseClient): EnlilSource {
  const bucket = () => sb.storage.from(ENLIL_BUCKET);
  return {
    async manifest() {
      const { data, error } = await bucket().download(ENLIL_MANIFEST);
      if (error || !data) return null;
      return parseEnlilManifest(await data.text());
    },
    async frameUrl(f) {
      if (f.name) {
        const { data, error } = await bucket().download(f.name);
        if (!error && data && data.size > 0) return URL.createObjectURL(data);
      }
      return swpcFrameUrl(f);
    },
    release(url) {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    },
  };
}
